/**
 * Query keys do domínio enrollments (matrículas do aluno — `apps/crm`).
 *
 * A RAIZ É `"student-enrollments"`, e não `"enrollments"`, DE PROPÓSITO: é o
 * literal que `useStudentEnrollments` já usava, e há invalidações por PREFIXO
 * fora deste domínio que dependem dele — `useCancelarMensalidadeAutomatica`
 * (cancelar a mensalidade automática devolve a matrícula ao gerador) e
 * `useWellhubRepasse` (o "Adicionar como aluno" cria matrícula). Trocar a raiz
 * aqui deixaria essas duas invalidações órfãs: elas continuariam "passando",
 * sem nunca casar com a key da ficha — a tela ficaria com o estado velho, que
 * numa seção de COBRANÇA é pior do que um erro visível.
 *
 * Por isso cada builder abaixo devolve EXATAMENTE o tuple que já existia:
 *
 *   byStudent(id)         -> ["student-enrollments", id]
 *   aggregatorContext(id) -> ["student-enrollments", id, "aggregator-context"]
 *   billingGroup(id)      -> ["student-enrollments", id, "billing-group"]
 *   commitment(id)        -> ["student-enrollments", id, "commitment"]
 *   timeline(id, enr)     -> ["student-enrollments", id, "timeline", enr]
 *   contextoDoValor(id, c)-> ["student-enrollments", id, "contexto-do-valor", c]
 */
import { tuple } from "./core";

const root = ["student-enrollments"] as const;

export const enrollments = {
  /** Prefixo do domínio — invalida a ficha inteira (lista + contexto). */
  all: root,
  /** Matrículas ativas de um aluno (`student_enrollments` + plano). */
  byStudent: (studentId: string | null | undefined) => tuple(root, studentId),
  /**
   * Contexto do "via agregador" da ficha: tag do aluno, regra do clube e se o
   * clube tem integração de agregador ligada. Consulta à parte da lista para
   * que uma falhar não derrube a outra — por isso ela é um SUFIXO da key do
   * aluno, e não uma raiz própria: invalidar o aluno invalida as duas.
   */
  aggregatorContext: (studentId: string | null | undefined) =>
    tuple(root, studentId, "aggregator-context"),
  /**
   * O UNIVERSO DO GERADOR para este aluno: os planos com vínculo de turma
   * aberto (`club_lesson_students.left_at IS NULL`) em plano `monthly_recurring`
   * ativo — o mesmo conjunto do CTE `effective_day` de
   * `get_students_due_for_billing`. É o que decide se a retomada da cobrança
   * produz mensalidade e quem mais entra no grupo (aluno + dia efetivo) cuja
   * âncora ela levanta.
   *
   * Sufixo da key do aluno pelo mesmo motivo do contexto: invalidar o aluno
   * invalida as três, e as invalidações por prefixo de fora continuam valendo.
   */
  billingGroup: (studentId: string | null | undefined) =>
    tuple(root, studentId, "billing-group"),
  /**
   * A PRÉVIA DO ENCERRAMENTO de UMA matrícula
   * (`previa_encerramento_matricula`, 20260925120000): quantas faturas futuras
   * caem, quanto valor, quantos vínculos de turma fecham e o que continua em
   * aberto.
   *
   * Chaveada pela MATRÍCULA, e não pelo aluno — o aluno pode ter várias, e cada
   * uma tem a sua conta. Por isso ela NÃO é sufixo de `byStudent(...)`: quem
   * encerra tem que invalidar `qk.enrollments.all` (o root) para derrubar a
   * prévia junto com a lista. Invalidar só `byStudent(...)` deixaria o diálogo
   * prometendo um cancelamento que já aconteceu.
   */
  encerramentoPreview: (enrollmentId: string | null | undefined) =>
    tuple(root, "encerramento-preview", enrollmentId),
  /**
   * As faturas de SÉRIE de fidelidade do aluno (`commitment_cycle` preenchido),
   * para a ficha mostrar "parcela X de N" e as datas do aviso e da decisão
   * (`apps/crm/src/lib/fidelidade.ts`). Sufixo da key do aluno: invalidar o
   * aluno (trocar o fim da fidelidade, encerrar) invalida esta também.
   */
  commitment: (studentId: string | null | undefined) =>
    tuple(root, studentId, "commitment"),
  /**
   * Histórico de UMA matrícula (RPC get_enrollment_timeline, 20261005160000),
   * no Gerenciar. Sufixo da key do ALUNO: as mutações da matrícula invalidam
   * `byStudent(studentId)` e, por prefixo, derrubam esta também.
   */
  timeline: (studentId: string | null | undefined, enrollmentId: string | null | undefined) =>
    tuple(root, studentId, "timeline", enrollmentId),
  /**
   * Contexto da ADOÇÃO POR VALOR para o valor da mensalidade no Gerenciar
   * (`fatura_aguardando_mensalidade`, 20261005160400): faturas soltas do aluno
   * no clube + o último ciclo gerado. Sufixo da key do aluno: toda mutação da
   * matrícula invalida `byStudent(...)` e derruba esta também.
   */
  contextoDoValor: (studentId: string | null | undefined, clubId: string | null | undefined) =>
    tuple(root, studentId, "contexto-do-valor", clubId),
  /**
   * O aluno JÁ É COBRADO por este plano? (matrícula ativa/pendente OU vínculo
   * de turma aberto no plano) + CPF do aluno + estratégia/pro-rata do clube —
   * o que a gaveta da turma precisa para decidir entre "só vincular" e abrir
   * "Matricular e cobrar". Sufixo da key do aluno: invalidar o aluno (ou o
   * root, como faz a própria matrícula) derruba esta também.
   */
  situacaoNoPlano: (
    studentId: string | null | undefined,
    tuitionPlanId: string | null | undefined,
  ) => tuple(root, studentId, "situacao-no-plano", tuitionPlanId),
} as const;
